import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  DatePicker,
  Row,
  Col,
  Tag,
  Typography,
  Empty,
  Tooltip,
  Popconfirm,
  App,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import RobinPage from "../components/RobinPage";
import {
  PlusOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useFinanceTransactions } from "../hooks/useFinanceTransactions";

const { RangePicker } = DatePicker;
const { Text } = Typography;

// Interface for finance transaction
interface FinanceTransaction {
  id: number;
  transactionNumber: string;
  date: string;
  description: string;
  totalDebit: number;
  totalCredit: number;
  total: number;
  imageId?: number | null;
  isBalanced: boolean;
  createdAt: string;
  updatedAt: string;
}

function FinanceTransactionList() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const navigate = useNavigate();
  const { message: messageApi } = App.useApp();

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page on search
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Build filter object - only include non-null values
  const filter = {
    ...(dateRange[0] && { dateFrom: dateRange[0].format("YYYY-MM-DD") }),
    ...(dateRange[1] && { dateTo: dateRange[1].format("YYYY-MM-DD") }),
    ...(debouncedSearchTerm && { description: debouncedSearchTerm }),
  };

  // Only pass filter if it has at least one property
  const hasFilters = Object.keys(filter).length > 0;

  // Fetch transactions using GraphQL
  const { data, loading, refetch } = useFinanceTransactions(
    pageSize,
    currentPage,
    hasFilters ? filter : undefined
  );

  const transactions = data?.getAllFinanceTreeTransactions?.transactions || [];

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      // TODO: Call GraphQL mutation to delete transaction
      console.log("Deleting transaction:", id);
      messageApi.success(t("message.success.data_deleted"));
      // Refetch the list after deletion
      refetch();
    } catch (error: any) {
      messageApi.error(error.message || t("message.error.data_delete_failed"));
    }
  };

  // Handle pagination change
  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setDateRange([null, null]);
    setCurrentPage(1);
  };

  // Table columns
  const columns: ColumnsType<FinanceTransaction> = [
    {
      title: <Text strong>{t("label.finance_transaction.transaction_number")}</Text>,
      dataIndex: "transactionNumber",
      key: "transactionNumber",
      width: 180,
      render: (transactionNumber: string) => (
        <Text strong style={{ color: "#1890ff" }}>
          {transactionNumber}
        </Text>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.date")}</Text>,
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (date: string) => (
        <Text>{dayjs(date).format("DD/MM/YYYY")}</Text>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.description")}</Text>,
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description: string, record) => (
        <Space size="small">
          {record.imageId && (
            <Tooltip title={t("label.finance_transaction.has_attachments")}>
              <FileTextOutlined style={{ color: "#1890ff" }} />
            </Tooltip>
          )}
          <Text>{description}</Text>
        </Space>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.total_debit")}</Text>,
      dataIndex: "totalDebit",
      key: "totalDebit",
      width: 130,
      align: "right",
      render: (amount: number) => (
        <Text strong style={{ color: "#cf1322" }}>
          {amount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.total_credit")}</Text>,
      dataIndex: "totalCredit",
      key: "totalCredit",
      width: 130,
      align: "right",
      render: (amount: number) => (
        <Text strong style={{ color: "#3f8600" }}>
          {amount.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.status")}</Text>,
      dataIndex: "isBalanced",
      key: "isBalanced",
      width: 100,
      align: "center",
      render: (isBalanced: boolean) => (
        <Tag color={isBalanced ? "success" : "warning"}>
          {isBalanced ? t("label.balanced") : t("label.unbalanced")}
        </Tag>
      ),
    },
    {
      title: <Text strong>{t("label.finance_transaction.created_at")}</Text>,
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (date: string) => (
        <Text type="secondary">
          {dayjs(date).format("DD/MM/YYYY HH:mm")}
        </Text>
      ),
    },
    {
      title: <Text strong>{t("label.actions")}</Text>,
      key: "actions",
      width: 150,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={t("button.view")}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/finance/transactions/view/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={t("button.edit")}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/finance/transactions/edit/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title={t("button.delete")}>
            <Popconfirm
              title={t("message.confirm_delete")}
              description={t("message.confirm_delete_description")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("button.confirm")}
              cancelText={t("button.cancel")}
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <RobinPage
      title={t("label.menu.finance_transactions_list")}
      actions={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/finance/transactions/create")}
          size="large"
        >
          {t("button.add_transaction")}
        </Button>
      }
    >
      <Card
        bordered={false}
        style={{
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        }}
        title={
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 16 }}>
              {t("label.menu.finance_transactions_list")}
            </Text>
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("label.finance_transaction_list_description")}
            </Text>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={10}>
            <Input
              placeholder={t("placeholder.search_finance_transactions")}
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              size="large"
              style={{
                borderRadius: 8,
                height: 44,
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={10}>
            <RangePicker
              style={{ 
                width: "100%",
                borderRadius: 8,
                height: 44,
              }}
              format="DD/MM/YYYY"
              placeholder={[
                t("placeholder.start_date"),
                t("placeholder.end_date"),
              ]}
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
              size="large"
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              block
              size="large"
              style={{
                borderRadius: 8,
                height: 44,
              }}
              onClick={handleResetFilters}
            >
              {t("button.reset_filters")}
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={transactions}
          loading={loading}
          rowKey="id"
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: transactions.length, // Note: Backend should return total count
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t("label.transactions")}`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          locale={{
            emptyText: (
              <Empty
                description={
                  searchTerm || dateRange[0] || dateRange[1]
                    ? t("label.no_search_results")
                    : t("label.no_finance_transactions")
                }
              />
            ),
          }}
          bordered
          scroll={{ x: 1400 }}
        />
      </Card>
    </RobinPage>
  );
}

export default FinanceTransactionList;

